import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { LanguageSelector } from '@/components/LanguageSelector'
import { formatDate } from '@/lib/format'

/**
 * The legal documents are templates: they describe the data this application
 * actually processes, but every company-specific detail is left as an explicit
 * `[PLACEHOLDER]` for a lawyer to fill in before publication.
 */
const LAST_UPDATED = '2026-09-22'

/** One numbered chapter of a legal document, as authored in the `legal` namespace. */
interface LegalSection {
  heading: string
  /** Prose shown before the bullet list. */
  paragraphs?: string[]
  /** Bullet list. */
  items?: string[]
  /** Prose shown after the bullet list. */
  afterItems?: string[]
}

function Paragraphs({ texts }: { texts: string[] }) {
  return (
    <>
      {texts.map((text) => (
        <p key={text} className="mb-3 last:mb-0">
          {text}
        </p>
      ))}
    </>
  )
}

/**
 * Public, unauthenticated page rendering one legal document (privacy policy or
 * terms of service) from the `legal` translation namespace, so both languages
 * stay in sync with the rest of the app's language switch.
 */
export function LegalDocument({ documentKey }: { documentKey: 'privacy' | 'terms' }) {
  const { t } = useTranslation(['legal', 'common'])
  const sections = t(`${documentKey}.sections`, { returnObjects: true }) as unknown as LegalSection[]

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <Link to="/" className="text-lg font-bold text-gray-900">
            {t('common:appName')}
          </Link>
          <LanguageSelector compact />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10">
        <div
          role="note"
          className="mb-8 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-semibold">{t('draftNotice.title')}</p>
            <p className="mt-1">{t('draftNotice.body')}</p>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900">{t(`${documentKey}.title`)}</h1>
        <p className="mt-2 text-muted-foreground">{t(`${documentKey}.subtitle`)}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('lastUpdated', { date: formatDate(LAST_UPDATED, { dateStyle: 'long' }) })}
        </p>

        <div className="mt-8 space-y-8 text-sm leading-relaxed text-gray-700">
          {sections.map((section) => (
            <section key={section.heading}>
              <h2 className="mb-3 text-lg font-semibold text-gray-900">{section.heading}</h2>
              {section.paragraphs && <Paragraphs texts={section.paragraphs} />}
              {section.items && (
                <ul className="my-3 list-disc space-y-2 pl-5">
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              {section.afterItems && <Paragraphs texts={section.afterItems} />}
            </section>
          ))}
        </div>

        <Link to="/" className="mt-10 inline-block text-sm text-primary hover:underline">
          {t('backToApp')}
        </Link>
      </main>
    </div>
  )
}
